import { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Save, X, DollarSign, Package, Upload, ImageIcon, Layers, Building2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { toast } from 'sonner';
import { formatGs, productImg } from '@/utils/helpers';

const emptyProduct = { name: '', description: '', category_id: '', image_url: '', active_global: true, variant_required: false, exclude_from_discounts: false, exclude_from_loyalty: false };

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list'); // list | pricing | addons | variants
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [branchPrices, setBranchPrices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [addonForm, setAddonForm] = useState({ name: '', extra_cost: 0, active: true });
  const [variants, setVariants] = useState([]);
  const [variantForm, setVariantForm] = useState({ name: '', price: '', sort_order: 0 });
  const [variantBranchPrices, setVariantBranchPrices] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTarget, setUploadTarget] = useState(null);
  const fileRef = useRef(null);

  const triggerUpload = (productId) => {
    setUploadTarget(productId);
    fileRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Solo jpg, png o webp'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await api.post(`/uploads/producto/${uploadTarget}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => setUploadProgress(Math.round((ev.loaded * 100) / ev.total)),
      });
      toast.success('Imagen subida');
      fetch();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al subir imagen'); }
    setUploading(false);
    setUploadProgress(0);
    setUploadTarget(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const fetch = () => {
    setLoading(true);
    Promise.all([
      api.get('/productos'),
      api.get('/categorias'),
      api.get('/sucursales'),
    ]).then(([p, c, b]) => {
      setProducts(p.data.productos);
      setCategories(c.data.categorias);
      setBranches(b.data.sucursales);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(fetch, []);

  const save = async () => {
    try {
      const payload = { ...form, category_id: parseInt(form.category_id) || null };
      if (editing === 'new') {
        await api.post('/productos', payload);
        toast.success('Producto creado');
      } else {
        await api.put(`/productos/${editing.id}`, payload);
        toast.success('Producto actualizado');
      }
      setEditing(null);
      setTab('list');
      fetch();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar producto?')) return;
    try { await api.delete(`/productos/${id}`); toast.success('Eliminado'); fetch(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // Branch pricing
  const loadBranchPrices = async (product) => {
    setSelectedProduct(product);
    setTab('pricing');
    const prices = [];
    for (const b of branches) {
      try {
        const { data } = await api.get(`/productos/sucursal/${b.id}`);
        const bp = data.productos_sucursal?.find(p => p.product_id === product.id);
        prices.push({ branch_id: b.id, branch_name: b.name, price: bp?.price || '', active: bp?.active ?? false, exists: !!bp });
      } catch { prices.push({ branch_id: b.id, branch_name: b.name, price: '', active: false, exists: false }); }
    }
    setBranchPrices(prices);
  };

  const saveBranchPrice = async (bp) => {
    try {
      await api.put(`/productos/sucursal/${bp.branch_id}/${selectedProduct.id}`, { price: parseFloat(bp.price) || 0, active: bp.active });
      toast.success(`Precio ${bp.branch_name} actualizado`);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // Addons
  const loadAddons = async (product) => {
    setSelectedProduct(product);
    setTab('addons');
    try {
      const { data } = await api.get(`/productos/${product.id}`);
      setAddons(data.producto?.addons || []);
    } catch { setAddons([]); }
  };

  const saveAddon = async () => {
    try {
      await api.post(`/productos/${selectedProduct.id}/complementos`, { ...addonForm, extra_cost: parseFloat(addonForm.extra_cost) || 0 });
      toast.success('Complemento creado');
      setAddonForm({ name: '', extra_cost: 0, active: true });
      loadAddons(selectedProduct);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const removeAddon = async (addonId) => {
    try {
      await api.delete(`/productos/${selectedProduct.id}/complementos/${addonId}`);
      toast.success('Complemento eliminado');
      loadAddons(selectedProduct);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // Variants
  const loadVariants = async (product) => {
    setSelectedProduct(product);
    setTab('variants');
    try {
      const { data } = await api.get(`/productos/${product.id}/variantes`);
      setVariants(data.variantes || []);
    } catch { setVariants([]); }
  };

  const saveVariant = async () => {
    if (!variantForm.name || !variantForm.price) { toast.error('Nombre y precio son obligatorios.'); return; }
    try {
      await api.post(`/productos/${selectedProduct.id}/variantes`, { ...variantForm, price: parseFloat(variantForm.price) || 0 });
      toast.success('Variante creada');
      setVariantForm({ name: '', price: '', sort_order: 0 });
      loadVariants(selectedProduct);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const removeVariant = async (vId) => {
    try {
      await api.delete(`/productos/${selectedProduct.id}/variantes/${vId}`);
      toast.success('Variante eliminada');
      loadVariants(selectedProduct);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // Variant branch prices
  const loadVariantBranchPrices = async (variant) => {
    setSelectedVariant(variant);
    setTab('variant-branch-prices');
    const prices = [];
    for (const b of branches) {
      prices.push({ branch_id: b.id, branch_name: b.name, price: '', has_override: false });
    }
    try {
      const { data } = await api.get(`/productos/${selectedProduct.id}/variantes/sucursal/0`);
      // Ignore, we fetch per branch below
    } catch {}
    // Get existing overrides
    try {
      const { data } = await api.get(`/productos/variantes/${variant.id}/precios-sucursal`);
      const overrides = {};
      (data.precios || []).forEach(p => { overrides[p.branch_id] = p.price; });
      prices.forEach(p => {
        if (overrides[p.branch_id] !== undefined) {
          p.price = overrides[p.branch_id];
          p.has_override = true;
        }
      });
    } catch {}
    setVariantBranchPrices(prices);
  };

  const saveVariantBranchPrice = async (bp) => {
    if (!bp.price && bp.price !== 0) { toast.error('Ingresá un precio'); return; }
    try {
      await api.post(`/productos/variantes/${selectedVariant.id}/precios-sucursal`, { branch_id: bp.branch_id, price: parseFloat(bp.price) });
      toast.success(`Precio ${bp.branch_name} actualizado`);
      loadVariantBranchPrices(selectedVariant);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const removeVariantBranchPrice = async (bp) => {
    try {
      await api.delete(`/productos/variantes/${selectedVariant.id}/precios-sucursal/${bp.branch_id}`);
      toast.success(`Precio especial ${bp.branch_name} eliminado`);
      loadVariantBranchPrices(selectedVariant);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (tab === 'pricing' && selectedProduct) {
    return (
      <div data-testid="admin-product-pricing">
        <button onClick={() => setTab('list')} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4"><X size={14} /> Volver a productos</button>
        <h2 className="text-lg font-bold mb-1">Precios por sucursal: <span className="text-[#E84C1C]">{selectedProduct.name}</span></h2>
        <p className="text-sm text-zinc-500 mb-4">Configura precio y disponibilidad en cada sucursal</p>
        <div className="space-y-3">
          {branchPrices.map((bp, i) => (
            <div key={bp.branch_id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium w-48">{bp.branch_name}</span>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">Precio (Gs.)</Label>
                <Input type="number" value={bp.price} onChange={e => setBranchPrices(prev => prev.map((p, j) => j === i ? { ...p, price: e.target.value } : p))} className="bg-zinc-950 border-zinc-800 h-8 w-32 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={bp.active} onChange={e => setBranchPrices(prev => prev.map((p, j) => j === i ? { ...p, active: e.target.checked } : p))} className="accent-[#E84C1C]" /> Activo</label>
              <button onClick={() => saveBranchPrice(bp)} className="text-xs bg-[#E84C1C] text-white px-3 py-1.5 rounded-lg hover:bg-[#d43f12]"><Save size={12} /></button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tab === 'addons' && selectedProduct) {
    return (
      <div data-testid="admin-product-addons">
        <button onClick={() => setTab('list')} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4"><X size={14} /> Volver a productos</button>
        <h2 className="text-lg font-bold mb-4">Complementos: <span className="text-[#E84C1C]">{selectedProduct.name}</span></h2>
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div><Label className="text-xs text-zinc-500">Nombre</Label><Input value={addonForm.name} onChange={e => setAddonForm(f => ({ ...f, name: e.target.value }))} className="bg-zinc-950 border-zinc-800 h-8 mt-1 w-48" /></div>
          <div><Label className="text-xs text-zinc-500">Costo extra (Gs.)</Label><Input type="number" value={addonForm.extra_cost} onChange={e => setAddonForm(f => ({ ...f, extra_cost: e.target.value }))} className="bg-zinc-950 border-zinc-800 h-8 mt-1 w-32" /></div>
          <button onClick={saveAddon} className="bg-[#E84C1C] text-white text-sm px-3 py-1.5 rounded-lg hover:bg-[#d43f12]"><Plus size={14} /></button>
        </div>
        <div className="space-y-2">
          {addons.map(a => (
            <div key={a.id} className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-3 flex items-center justify-between">
              <div><span className="text-sm">{a.name}</span> <span className="text-xs text-[#E84C1C] ml-2">+{formatGs(a.extra_cost)}</span></div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${a.active ? 'text-emerald-400' : 'text-red-400'}`}>{a.active ? 'Activo' : 'Inactivo'}</span>
                <button onClick={() => removeAddon(a.id)} className="text-zinc-400 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {addons.length === 0 && <p className="text-sm text-zinc-500">Sin complementos.</p>}
        </div>
      </div>
    );
  }

  if (tab === 'variant-branch-prices' && selectedVariant && selectedProduct) {
    return (
      <div data-testid="admin-variant-branch-prices">
        <button onClick={() => { setTab('variants'); setSelectedVariant(null); }} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4"><X size={14} /> Volver a variantes</button>
        <h2 className="text-lg font-bold mb-1">Precios por sucursal: <span className="text-[#E84C1C]">{selectedVariant.name}</span></h2>
        <p className="text-sm text-zinc-500 mb-1">Producto: {selectedProduct.name}</p>
        <p className="text-xs text-zinc-600 mb-4">Precio default: <span className="text-zinc-400 font-mono">{formatGs(selectedVariant.price_default || selectedVariant.price)}</span> — Si no configurás un precio para una sucursal, se usa el default.</p>
        <div className="space-y-3">
          {variantBranchPrices.map((bp, i) => (
            <div key={bp.branch_id} data-testid={`vbp-branch-${bp.branch_id}`} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium w-48 flex items-center gap-2">
                <Building2 size={14} className="text-zinc-500" />
                {bp.branch_name}
              </span>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">Precio (Gs.)</Label>
                <Input
                  type="number"
                  value={bp.price}
                  onChange={e => setVariantBranchPrices(prev => prev.map((p, j) => j === i ? { ...p, price: e.target.value } : p))}
                  placeholder={String(selectedVariant.price_default || selectedVariant.price)}
                  className="bg-zinc-950 border-zinc-800 h-8 w-32 text-sm"
                  data-testid={`vbp-price-${bp.branch_id}`}
                />
              </div>
              {bp.has_override && <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full">Personalizado</span>}
              <div className="flex items-center gap-2">
                <button onClick={() => saveVariantBranchPrice(bp)} className="text-xs bg-[#E84C1C] text-white px-3 py-1.5 rounded-lg hover:bg-[#d43f12]" data-testid={`vbp-save-${bp.branch_id}`}><Save size={12} /></button>
                {bp.has_override && (
                  <button onClick={() => removeVariantBranchPrice(bp)} className="text-xs text-zinc-400 hover:text-red-400 px-2 py-1.5" title="Usar precio default" data-testid={`vbp-remove-${bp.branch_id}`}><Trash2 size={12} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tab === 'variants' && selectedProduct) {
    return (
      <div data-testid="admin-product-variants">
        <button onClick={() => setTab('list')} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4"><X size={14} /> Volver a productos</button>
        <h2 className="text-lg font-bold mb-4">Variantes: <span className="text-[#E84C1C]">{selectedProduct.name}</span></h2>
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div><Label className="text-xs text-zinc-500">Nombre</Label><Input value={variantForm.name} onChange={e => setVariantForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: 5 piezas" className="bg-zinc-950 border-zinc-800 h-8 mt-1 w-40" data-testid="variant-name" /></div>
          <div><Label className="text-xs text-zinc-500">Precio (Gs.)</Label><Input type="number" value={variantForm.price} onChange={e => setVariantForm(f => ({ ...f, price: e.target.value }))} className="bg-zinc-950 border-zinc-800 h-8 mt-1 w-32" data-testid="variant-price" /></div>
          <div><Label className="text-xs text-zinc-500">Orden</Label><Input type="number" value={variantForm.sort_order} onChange={e => setVariantForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 h-8 mt-1 w-20" /></div>
          <button onClick={saveVariant} data-testid="variant-save-btn" className="bg-[#E84C1C] text-white text-sm px-3 py-1.5 rounded-lg hover:bg-[#d43f12]"><Plus size={14} /></button>
        </div>
        <div className="space-y-2">
          {variants.map(v => (
            <div key={v.id} data-testid={`admin-variant-${v.id}`} className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-3 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium">{v.name}</span>
                <span className="text-xs text-[#E84C1C] ml-2 font-mono">{formatGs(v.price)}</span>
                {v.price_default && v.price_default !== v.price && <span className="text-[10px] text-zinc-600 ml-1">(default: {formatGs(v.price_default)})</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => loadVariantBranchPrices(v)} className="text-zinc-400 hover:text-amber-400 transition-colors" title="Precios por sucursal" data-testid={`variant-branch-prices-${v.id}`}><Building2 size={14} /></button>
                <span className={`text-xs ${v.active ? 'text-emerald-400' : 'text-red-400'}`}>{v.active ? 'Activo' : 'Inactivo'}</span>
                <button onClick={() => removeVariant(v.id)} className="text-zinc-400 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {variants.length === 0 && <p className="text-sm text-zinc-500">Sin variantes. Agregá variantes si el producto tiene diferentes tamaños o presentaciones.</p>}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Productos</h2>
          <p className="text-sm text-zinc-500">{products.length} productos</p>
        </div>
        <button onClick={() => { setEditing('new'); setForm(emptyProduct); }} className="flex items-center gap-2 bg-[#E84C1C] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d43f12] transition-colors" data-testid="product-add-btn">
          <Plus size={16} /> Nuevo
        </button>
      </div>

      {editing && (
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-6 space-y-3" data-testid="product-form">
          <h3 className="text-sm font-semibold">{editing === 'new' ? 'Nuevo producto' : `Editar: ${editing.name}`}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Nombre</Label><Input value={form.name} onChange={e => set('name', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" data-testid="product-name" /></div>
            <div>
              <Label className="text-xs text-zinc-500">Categoria</Label>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 h-9 text-sm text-zinc-300 outline-none mt-1">
                <option value="">Sin categoria</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Descripcion</Label><Textarea value={form.description} onChange={e => set('description', e.target.value)} className="bg-zinc-950 border-zinc-800 mt-1" rows={2} /></div>
            <div><Label className="text-xs text-zinc-500">Image URL</Label><Input value={form.image_url} onChange={e => set('image_url', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" /></div>
            <div className="flex items-end gap-4 pb-1 flex-wrap">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active_global} onChange={e => set('active_global', e.target.checked)} className="accent-[#E84C1C]" /> Activo global</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.variant_required} onChange={e => set('variant_required', e.target.checked)} className="accent-amber-500" /> Variante obligatoria</label>
              <label className="flex items-center gap-2 text-sm" data-testid="exclude-discounts"><input type="checkbox" checked={form.exclude_from_discounts} onChange={e => set('exclude_from_discounts', e.target.checked)} className="accent-red-500" /> Excluir de descuentos</label>
              <label className="flex items-center gap-2 text-sm" data-testid="exclude-loyalty"><input type="checkbox" checked={form.exclude_from_loyalty} onChange={e => set('exclude_from_loyalty', e.target.checked)} className="accent-red-500" /> Excluir de fidelización</label>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="flex items-center gap-1 bg-[#E84C1C] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#d43f12]" data-testid="product-save"><Save size={14} /> Guardar</button>
            <button onClick={() => setEditing(null)} className="flex items-center gap-1 text-sm text-zinc-400 px-3 py-2 rounded-lg hover:bg-zinc-800"><X size={14} /> Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-800/50 text-zinc-500 text-xs uppercase tracking-wider">
              <th className="text-left p-3 font-medium">Producto</th><th className="text-left p-3 font-medium">Categoria</th><th className="text-center p-3 font-medium">Activo</th><th className="text-right p-3 font-medium">Acciones</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} className="p-6 text-center text-zinc-500">Cargando...</td></tr> :
              products.map(p => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-zinc-800/30 hover:bg-zinc-800/20">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.image_url ? <img src={productImg(p.image_url, 'thumb')} alt="" className="w-10 h-10 rounded-lg object-cover bg-zinc-800" loading="lazy" /> : <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-600"><ImageIcon size={16} /></div>}
                      <div><p className="font-medium">{p.name}</p><p className="text-xs text-zinc-500 truncate max-w-[200px]">{p.description}</p></div>
                    </div>
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{p.category_name || '-'}</td>
                  <td className="p-3 text-center">{p.active_global ? <span className="text-emerald-400 text-xs">Si</span> : <span className="text-red-400 text-xs">No</span>}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => triggerUpload(p.id)} className="text-zinc-400 hover:text-amber-400 transition-colors" title="Subir imagen" data-testid={`product-upload-${p.id}`}>
                        <Upload size={14} />
                      </button>
                      {uploading && uploadTarget === p.id && <span className="text-[10px] text-amber-400">{uploadProgress}%</span>}
                      <button onClick={() => loadBranchPrices(p)} className="text-zinc-400 hover:text-emerald-400 transition-colors" title="Precios"><DollarSign size={14} /></button>
                      <button onClick={() => loadAddons(p)} className="text-zinc-400 hover:text-blue-400 transition-colors" title="Complementos"><Package size={14} /></button>
                      <button onClick={() => loadVariants(p)} className="text-zinc-400 hover:text-amber-400 transition-colors" title="Variantes" data-testid={`product-variants-${p.id}`}><Layers size={14} /></button>
                      <button onClick={() => { setEditing(p); setForm({ name: p.name, description: p.description || '', category_id: p.category_id || '', image_url: p.image_url || '', active_global: p.active_global, variant_required: p.variant_required || false, exclude_from_discounts: p.exclude_from_discounts || false, exclude_from_loyalty: p.exclude_from_loyalty || false }); }} className="text-zinc-400 hover:text-[#E84C1C] transition-colors"><Pencil size={14} /></button>
                      <button onClick={() => remove(p.id)} className="text-zinc-400 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Hidden file input for image upload */}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
    </div>
  );
}
